/**
 * Taschengeld (E20, Schritt 2): ein kleines Konto des Kindes, getrennt von
 * den Haushaltskosten, die Kinder nie sehen.
 *
 * Alles steht als **Zeilen** da, nie als Zähler — zwei Geräte zählten sonst
 * gegeneinander. Der Stand ist Einnahmen minus Ausgaben. Geld in ganzen Cent.
 */
import { addDays } from './time.js';
import { stableId } from './ids.js';

/**
 * `allowance` bucht der Server nach der Regel der Eltern, `gift` tragen
 * Erwachsene ein (Geburtstag, Extra), `expense` das Kind selbst — „was ich
 * gekauft habe“. `save` und `withdraw` (24.10.2026) schieben Geld zwischen
 * dem freien Geld und einem Sparziel hin und her; am Gesamtstand ändern sie
 * nichts. `reward` bucht der Server für eine erledigte Aufgabe mit Betrag.
 */
export const POCKET_KINDS = ['allowance', 'gift', 'expense', 'save', 'withdraw', 'reward'] as const;
export type PocketKind = (typeof POCKET_KINDS)[number];

/** Was nur der Server bucht — nie ein Gerät, sonst schriebe sich jeder etwas gut. */
export const SERVER_POCKET_KINDS = ['allowance', 'reward'] as const;

/** Was ein Kind (ab `school`) für sich selbst einträgt. */
export const CHILD_POCKET_KINDS = ['expense', 'save', 'withdraw'] as const;

/** Umbuchungen brauchen ein Sparziel — sonst wüsste niemand, wohin. */
export function pocketNeedsGoal(kind: string): boolean {
  return kind === 'save' || kind === 'withdraw';
}

/** Name und Betrag eines Sparziels: höchstens so lang wie eine Notiz. */
export const POCKET_GOAL_NAME_MAX = 60;

export const ALLOWANCE_INTERVALS = ['weekly', 'monthly'] as const;
export type AllowanceInterval = (typeof ALLOWANCE_INTERVALS)[number];

export interface AllowanceRule {
  amountCents: number;
  interval: AllowanceInterval;
  /** Wöchentlich: 1 = Montag … 7 = Sonntag. Monatlich: Tag 1–28. */
  day: number;
  /** Ab hier wird gebucht — nie rückwirkend vor dem Einrichten. */
  since: string;
}

interface PocketRow {
  kind: string;
  amountCents: number;
  goalId?: string | null;
  deletedAt?: string | null;
}

/** Gesamtstand: Einnahmen plus, Ausgaben minus; Umbuchungen zählen nicht. */
export function pocketBalance(entries: readonly PocketRow[]): number {
  let cents = 0;
  for (const entry of entries) {
    if (entry.deletedAt || pocketNeedsGoal(entry.kind)) continue;
    cents += (entry.kind === 'expense' ? -1 : 1) * entry.amountCents;
  }
  return cents;
}

export interface PocketSummary {
  /** Was das Kind ausgeben kann, ohne an ein Sparziel zu gehen. */
  free: number;
  /** Gespartes je Sparziel (nur lebende Ziele). */
  saved: Map<string, number>;
  /** free + alles Gesparte — gleich `pocketBalance`. */
  total: number;
}

/**
 * Freies Geld und Gespartes je Ziel. Zeigt eine Buchung auf ein Ziel, das es
 * nicht (mehr) gibt, zählt sie zum freien Geld — eine Umbuchung hebt sich dann
 * auf. So kehrt Gespartes beim Löschen des Ziels zurück, ohne dass jemand
 * etwas umbuchen muss, und nichts verschwindet.
 */
export function pocketSummary(
  entries: readonly PocketRow[],
  goalIds: Iterable<string>,
): PocketSummary {
  const saved = new Map<string, number>();
  for (const id of goalIds) saved.set(id, 0);
  let free = 0;
  for (const entry of entries) {
    if (entry.deletedAt) continue;
    const goal = entry.goalId && saved.has(entry.goalId) ? entry.goalId : null;
    const amount = entry.amountCents;
    if (pocketNeedsGoal(entry.kind)) {
      if (!goal) continue;
      const toGoal = entry.kind === 'save' ? amount : -amount;
      saved.set(goal, saved.get(goal)! + toGoal);
      free -= toGoal;
      continue;
    }
    const signed = (entry.kind === 'expense' ? -1 : 1) * amount;
    if (goal) saved.set(goal, saved.get(goal)! + signed);
    else free += signed;
  }
  let total = free;
  for (const cents of saved.values()) total += cents;
  return { free, saved, total };
}

/** Wochentag eines Kalendertags, 1 = Montag … 7 = Sonntag. */
function weekday(date: string): number {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/**
 * Fälligkeiten zwischen `since` und `today` (beide einschließlich). Monatlich
 * höchstens bis zum 28., damit es jeden Monat den Tag gibt.
 */
export function allowanceDueDates(rule: AllowanceRule, today: string): string[] {
  if (rule.amountCents <= 0 || rule.since > today) return [];
  const dates: string[] = [];
  let date = rule.since;
  // Obergrenze gegen eine Endlosschleife bei kaputten Daten: gut drei Jahre.
  for (let step = 0; step < 1200 && date <= today; step++) {
    const due =
      rule.interval === 'weekly'
        ? weekday(date) === rule.day
        : Number(date.slice(8, 10)) === Math.min(Math.max(rule.day, 1), 28);
    if (due) dates.push(date);
    date = addDays(date, 1);
  }
  return dates;
}

/** Feste Kennung einer Taschengeld-Buchung — gebucht wird genau einmal. */
export function allowanceEntryId(childId: string, date: string): string {
  return stableId(`allowance:${childId}:${date}`);
}

/**
 * Feste Kennung des Geldes für eine erledigte Aufgabe: je Aufgabe und Tag
 * genau eine Buchung. Mehrmals am selben Tag abhaken (bei Wiederholungen rückt
 * die Fälligkeit dabei weiter) bringt kein zweites Mal Geld; wieder öffnen und
 * neu abhaken trifft dieselbe Zeile.
 */
export function rewardEntryId(taskId: string, doneDate: string): string {
  return stableId(`reward:${taskId}:${doneDate}`);
}

/** Höchstbetrag je Aufgabe — ein Tippfehler soll nicht 500 € gutschreiben. */
export const MAX_REWARD_CENTS = 2000;
